
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, LinkIcon, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import NTBIntegration from '../interview/NTBIntegration';
import ContextArticleDisplay from '../interview/ContextArticleDisplay';
import HashtagGenerationButton from './HashtagGenerationButton';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ContentSectionProps {
  context: string;
  setContext: (value: string) => void;
  contextUrl: string;
  setContextUrl: (value: string) => void;
  contextImageUrl: string;
  setContextImageUrl: (value: string) => void;
  contextImageCaption: string;
  setContextImageCaption: (value: string) => void;
  question: string;
  setQuestion: (value: string) => void;
  answer: string;
  setAnswer: (value: string) => void;
  tags: string[];
  setTags: (value: string[]) => void;
  validTags: string[];
}

const ContentSection: React.FC<ContentSectionProps> = ({
  context,
  setContext,
  contextUrl,
  setContextUrl,
  contextImageUrl,
  setContextImageUrl,
  contextImageCaption,
  setContextImageCaption,
  question,
  setQuestion,
  answer,
  setAnswer,
  tags,
  setTags,
  validTags
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [externalTitle, setExternalTitle] = useState('');
  const [isFetchingTitle, setIsFetchingTitle] = useState(false);
  const [titleFetchStatus, setTitleFetchStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  // Helper function to check if URL is NTB article
  const isNTBArticle = (url: string) => {
    return url && url.startsWith('ntb:');
  };

  // Helper to check if URL is valid external URL
  const isValidExternalUrl = (url: string) => {
    return url && !url.startsWith('ntb:') && (url.startsWith('http://') || url.startsWith('https://'));
  };

  // Parse existing context to get title if it exists
  useEffect(() => {
    if (context) {
      try {
        const data = JSON.parse(context);
        if (data.type === 'external_article' && data.title) {
          setExternalTitle(data.title);
          setTitleFetchStatus('success');
        }
      } catch {
        // Not JSON, ignore
      }
    }
  }, []);

  // Fetch title when URL changes
  const fetchTitle = useCallback(async (url: string) => {
    if (!isValidExternalUrl(url)) return;
    
    setIsFetchingTitle(true);
    setTitleFetchStatus('idle');
    
    try {
      const { data, error } = await supabase.functions.invoke('fetch-url-title', {
        body: { url }
      });
      
      if (error) throw error;
      
      if (data?.success && data?.title) {
        setExternalTitle(data.title);
        setTitleFetchStatus('success');
        // Store as JSON in context
        setContext(JSON.stringify({
          type: 'external_article',
          title: data.title,
          url: url
        }));
        toast({
          title: 'Tittel hentet',
          description: data.title,
        });
      } else {
        setTitleFetchStatus('error');
        toast({
          title: 'Kunne ikke hente tittel',
          description: 'Skriv inn tittelen manuelt',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching title:', error);
      setTitleFetchStatus('error');
    } finally {
      setIsFetchingTitle(false);
    }
  }, [setContext, toast]);

  // Handle title change (manual edit)
  const handleTitleChange = (newTitle: string) => {
    setExternalTitle(newTitle);
    if (newTitle && contextUrl) {
      setContext(JSON.stringify({
        type: 'external_article',
        title: newTitle,
        url: contextUrl
      }));
    }
  };

  return (
    <div className="space-y-4">
      {/* NTB Integration */}
      <NTBIntegration 
        onArticleSelect={(article) => {
          setContext(article.context);
          setContextUrl(article.contextUrl);
          if (article.contextImageUrl) {
            setContextImageUrl(article.contextImageUrl);
          }
          if (article.contextImageCaption) {
            setContextImageCaption(article.contextImageCaption);
          }
        }}
        className="mb-4"
      />

      {/* Display context article if available */}
      {context && (
        <ContextArticleDisplay
          context={context}
          contextUrl={contextUrl}
          isNTBArticle={isNTBArticle(contextUrl)}
          className="mb-4"
        />
      )}

      <div>
        <div className="flex items-center gap-2 mb-1">
          <label htmlFor="contextUrl" className="block text-sm font-medium text-gray-700">
            Kontekst URL for ekstern artikkel (valgfri)
          </label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-80">
                <p>Legg til en ekstern URL. Hvis du vil bruke NTB, bruk NTB-integrasjonen over.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="relative">
          <input
            id="contextUrl"
            type="url"
            value={contextUrl}
            onChange={(e) => {
              const newUrl = e.target.value;
              setContextUrl(newUrl);
              // Reset title state when URL changes
              if (newUrl && isValidExternalUrl(newUrl)) {
                setExternalTitle('');
                setTitleFetchStatus('idle');
                // Auto-fetch title after a short delay
                const timeoutId = setTimeout(() => {
                  fetchTitle(newUrl);
                }, 500);
                return () => clearTimeout(timeoutId);
              }
            }}
            onBlur={(e) => {
              // Fetch title on blur if we haven't already
              const url = e.target.value;
              if (isValidExternalUrl(url) && !externalTitle && !isFetchingTitle) {
                fetchTitle(url);
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md pr-8"
            placeholder="https://example.com/article"
          />
          {contextUrl && (
            <LinkIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-500" />
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Hvis du legger til en URL, vil konteksten være klikkbar i artikkelen og åpne den eksterne siden.
        </p>
      </div>
      
      {/* External article title field - shows when we have a valid external URL */}
      {isValidExternalUrl(contextUrl) && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label htmlFor="externalTitle" className="block text-sm font-medium text-gray-700">
              Tittel på ekstern artikkel *
            </label>
            {isFetchingTitle && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {titleFetchStatus === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
            {titleFetchStatus === 'error' && <AlertCircle className="h-4 w-4 text-amber-500" />}
          </div>
          <input
            id="externalTitle"
            type="text"
            value={externalTitle}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="F.eks. Yeast er en liten restaurant med store smaker"
          />
          <p className="mt-1 text-xs text-gray-500">
            {titleFetchStatus === 'success' 
              ? 'Tittelen ble hentet automatisk. Du kan redigere den om nødvendig.'
              : titleFetchStatus === 'error'
              ? 'Kunne ikke hente tittel automatisk. Skriv inn tittelen manuelt.'
              : 'Tittelen hentes automatisk fra URL-en, eller du kan skrive den inn manuelt.'}
          </p>
        </div>
      )}
      
      <div>
        <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-1">
          {t('production.form.question')} *
        </label>
        <textarea
          id="question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          required
        />
      </div>
      
      <div>
        <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-1">
          {t('production.form.answer')} *
        </label>
        <textarea
          id="answer"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          required
        />
        <p className="mt-2 text-sm text-gray-500">
          {t('production.form.wordCount')}: {answer.trim() ? answer.trim().split(/\s+/).length : 0}
        </p>
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
            {t('production.form.tags')}
          </label>
          <HashtagGenerationButton
            question={question}
            answer={answer}
            onHashtagsGenerated={setTags}
          />
        </div>
        <input
          id="tags"
          type="text"
          value={tags.map(tag => tag.startsWith('#') ? tag : `#${tag}`).join(', ')}
          onChange={(e) => setTags(e.target.value.split(',').map(tag => tag.trim().replace(/^#/, '')).filter(tag => tag))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="#frontend, #backend, #leadership"
        />
        <p className="mt-1 text-sm text-gray-500">
          {t('production.form.validTags')}: {validTags.join(', ')}
        </p>
      </div>
    </div>
  );
};

export default ContentSection;
